import { useIntl } from 'react-intl';
import { ActionChip } from '@entur/chip';
import { LegacyColumns } from '../types';
import { ActiveFilter } from '../hooks/useClientFilter';
import styles from '../styles.module.scss';
import { buildColumnFilterOptions } from '../tableFeatures';
import { ReactNode } from 'react';

const MAX_INLINE_VALUES = 2;

interface ActiveFilterChipsProps {
  activeFilters: ActiveFilter[];
  columns: LegacyColumns;
  /** All current rows (unfiltered), needed to derive the same key→label mapping ClientFilterModal used to build the checkbox list */
  allRows: object[];
  clearColumnFilter: (columnKey: string) => void;
  filteredCount: number;
  totalCount: number;
}

export const ActiveFilterChips = ({
  activeFilters,
  columns,
  allRows,
  clearColumnFilter,
  filteredCount,
  totalCount,
}: ActiveFilterChipsProps) => {
  const intl = useIntl();

  if (activeFilters.length === 0) return null;

  const noValueLabel = intl.formatMessage({ id: 'generalValues.noValue' });

  return (
    <div className={styles.chipWrapper}>
      {activeFilters.map((f) => {
        const column = columns[f.columnKey];
        const label = (column?.text ?? f.columnKey).replace(/\|/g, '');
        let valueLabel: ReactNode = '';
        // parseObject columns show inline regardless of count; others cap at MAX_INLINE_VALUES.
        if (column && (column.parseObject || f.values.length <= MAX_INLINE_VALUES)) {
          // Same key→label map ClientFilterModal built for the checkbox list — a stored value
          // isn't always the raw cell value (e.g. "Pris" keys on its rendered label), so this
          // must resolve it the same way rather than re-deriving it independently.
          const options = buildColumnFilterOptions(f.columnKey, column, allRows, noValueLabel);
          const resolvedLabels = f.values.map((v) => options.get(v) ?? String(v));
          const hasNonPrimitiveLabel = resolvedLabels.some(
            (elem) => typeof elem !== 'string' && typeof elem !== 'number' && typeof elem !== 'boolean'
          );
          // only show the values if they are not objects (they can be if the parse function returns a React element)
          // and none of them resolved to an empty label (a stale/unmatched filter value)
          if (!hasNonPrimitiveLabel && resolvedLabels.every((elem) => elem !== '')) {
            valueLabel = resolvedLabels.flatMap((elem, i) => (i === 0 ? [elem] : [', ', elem]));
          }
        }
        if (!valueLabel) {
          // either too many values selected or the values are objects: show how many items instead
          valueLabel = `${f.values.length} ${intl.formatMessage({ id: 'generalValues.selected', defaultMessage: 'valgte' })}`;
        }

        return (
          <ActionChip key={f.columnKey} onClick={() => clearColumnFilter(f.columnKey)}>
            {label}: {valueLabel} ✕
          </ActionChip>
        );
      })}
      <span className={styles.filteredRowCount}>
        {filteredCount} {intl.formatMessage({ id: 'generalValues.of' })} {totalCount}{' '}
        {intl.formatMessage({ id: 'generalValues.rows' })}
      </span>
    </div>
  );
};
